<template>
  <div v-if="loading" class="flex col-12 p2 items-center justify-center">
    <Loader class="flex" />
  </div>
  <div v-else class="flex flex-wrap col-12 p2">
    <DetailedBreadcrumb class="p2 transparent" override-style :start-from="0" />
    <div class="flex flex-wrap col-12 p2 mb2 white-bg">
      <div class="flex col-12 justify-center center mb2 bold">Ma commande</div>
      <div class="flex col-12 justify-center center bold">{{ order.order.order_items[0].product_name }}</div>
      <div class="flex col-12 justify-center center">Date de la commande: {{ formatDate(order.date) }}</div>
      <div class="flex col-12 justify-center center mb2">Nº de commande: {{ order.uuid }}</div>
      <div class="flex col-12 justify-center center">Récapitulatif de paiement:</div>
      <div v-for="option in options" :key="option" class="flex col-12 justify-center center">{{ `${option.quantity} x ${option.name} - Total: ${option.price} € TTC` }}</div>
      <div v-if="recurring" class="flex col-12 justify-center center">Votre mensualité: {{ handlePrice(order.order.recurringTotal) }} € TTC / mois</div>
      <div v-else class="flex col-12 justify-center center">Total: {{ handlePrice(order.order.total) }} € TTC</div>
      <div v-if="recurring" class="flex col-12 justify-center center">Abonnement mensuel</div>
      <div v-for="(installment, i) in installments" :key="installment" class="flex col-12 justify-center center">{{ `${i+1}${i+1 === 1 ? 'er' : 'e'} paiement le ${formatDate(installment.date)} - ${paymentsPlatform === 'sharegroop' ? handlePrice(installment.price) : installment.price} € - ${installment.isPaid ? 'Payé' : 'À payer'}` }}</div>
    </div>
    <div v-if="routeMatchesComponent" class="flex flex-wrap col-12">
      <div class="flex flex-wrap col-12 p2 mb2 white-bg">
        <div v-pre class="flex col-12 justify-center center mb2 bold">Le(s) participant(s)</div>
        <div v-for="participant in participants" :key="participant" class="flex col-12 justify-center center">{{ participant.firstname }} {{ participant.lastname }}</div>
      </div>
      <div v-for="child in children" :key="child.name" class="flex justify-center col-12 mb1 white-bg p2 mb2 pointer relative items-center" @click="goToChild(child)">
        <div class="flex bold">
          {{ child.meta.title }}
        </div>
        <div class="flex items-center absolute right-0"><i class="flex right-0 items-center icon icon-chevron-right h1n"></i></div>
      </div>
    </div>

    <router-view v-else />
  </div>
</template>

<script>
import { getCurrentInstance, ref, computed, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { add, format } from 'date-fns';
import { routeIsCurrentComponent } from '@/mixins/utils';
import { getPriceInfo } from '@/mixins/requests';
import DetailedBreadcrumb from '../common/DetailedBreadcrumb.vue';
import Loader from '@/components/common/Loader';

export default {
  components: { Loader, DetailedBreadcrumb },
  setup () {
    const loading = ref(false);
    const router = useRouter();
    const route = useRoute();
    const routeMatchesComponent = computed(() => routeIsCurrentComponent(route.name, getCurrentInstance()));
    const store = useStore();
    const disabled_features = ref((store.state.siteInfo.perso_settings && store.state.siteInfo.perso_settings.disabled_features) ? store.state.siteInfo.perso_settings.disabled_features : []);
    const uuid = ref(route.params.uuid);
    const paymentsPlatform = computed(() => (store.state.siteInfo.payments_platform));
    const orders = computed(() => (store.state.user.orders || []));
    const order = computed(() => orders.value.find(o => o.uuid === uuid.value) || {});
    const priceInfo = ref({});
    const installments = computed(() => {
      switch (paymentsPlatform.value) {
        case 'sharegroop':
          return priceInfo.value.installments;
        default: {
          const inst = [];
          if (order.value.order.installment) {
            const payments = (order.value.order.payments || []).sort((a, b) => a.date - b.date);

            for (let i = 0; i < order.value.order.installment; i += 1) {
              inst.push({
                price: i === 0 ? priceInfo.value.firstPayment : priceInfo.value.othersPayments,
                date: payments[i] && payments[i].is_paid ? parseInt(payments[i].date) : add(new Date(order.value.date), { months: i }),
                isPaid: payments[i] && payments[i].is_paid
              });
            }
          }
          return inst;
        }
      }
    });

    const handlePrice = price => (parseInt(price) / 100).toFixed(2);

    const options = computed(() => {
      let opts = [];
      order.value.order.order_items.map(item => {
        if (item.options && item.options.length) {
          item.options = item.options.map(option => {
            option.price = handlePrice(option.price);
            return option;
          });
          opts = opts.concat(item.options);
        }
      });
      return opts;
    });

    const contacts = computed(() => store.state.contacts);
    
    const participants = computed(() => {
      const uniqueContacts = {};
      order.value.order.order_items.forEach(item => {
        const contact = contacts.value.find(c => c.uuid === item.customer.uuid);
        if (contact) {
          uniqueContacts[contact.uuid] = contact
        }
      });
      return Object.values(uniqueContacts);
    });

    const recurring = computed(() => order.value.order.order_items.some(item => item.recurring));

    const children = computed(() => (route.matched.find(r => r.name === route.name) || []).children.filter(c => {
      let enabled = !disabled_features.value.includes(c.path);
      switch (c.name) {
        case 'OrderReceipts':
          if (!(['sharegroop'].includes(paymentsPlatform.value))) {
            enabled = false;
          }
          break;
        default:
          return enabled;
      }
      return enabled;
    }));

    const formatDate = date => format(new Date(date), 'dd/MM/yyyy');

    const goToChild = child => {
      router.push({ ...child, props: { order } });
    };

    onBeforeMount(async () => {
      loading.value = true;

      await getPriceInfo({ orderUuid: order.value.uuid })
        .then(async r => {
          priceInfo.value = r;
        })
        .catch(err => ({ success: false, message: err }));
      loading.value = false;
    });

    return { order, routeMatchesComponent, children, options, handlePrice, participants, goToChild, installments, formatDate, recurring, loading, paymentsPlatform };
  }
};
</script>

<style>

</style>
